// ============================================================
// Faq.jsx
//
// Frequently Asked Questions page with a branded hero header
// and expandable accordion items.
//
// Clean Code:
//   - FAQ data is stored in a separate array (FAQ_DATA)
//   - Styled components are documented and named clearly
//   - Brand colors centralized in BRAND constant
// ============================================================

import React, { useState } from "react";
import {
    Box, Typography, Accordion, AccordionSummary,
    AccordionDetails, Container, Chip, styled,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────────
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SchoolIcon from "@mui/icons-material/School";
import PaymentIcon from "@mui/icons-material/Payment";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CodeIcon from "@mui/icons-material/Code";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DevicesIcon from "@mui/icons-material/Devices";

// ============================================================
//  CONSTANTS
// ============================================================

/**
 * Brand colors — consistent with the rest of the platform.
 */
const BRAND = {
    dark:  "#100720",
    gold:  "#E5C95F",
    white: "#FFFFFF",
    muted: "#475569",
    text:  "#1e293b",
    border:"#E2E8F0",
};

/**
 * FAQ_DATA
 * All FAQ entries with a category icon, question, and answer.
 * To add a new FAQ, add an object to this array — no JSX changes needed.
 *
 * Updated to reflect the actual platform offering:
 *   - Course access flow
 *   - Plans and features
 *   - Compiler, DSA, certification
 *   - Support tiers
 */
const FAQ_DATA = [
    {
        icon: <SchoolIcon fontSize="small" />,
        question: "Who is this program designed for?",
        answer: "Our programs are tailored for aspiring software developers, database administrators, and anyone looking to gain industry-standard coding skills across 16 professional course tracks — from Java and Python to Linux, IoT, and Software Testing.",
    },
    {
        icon: <LockOpenIcon fontSize="small" />,
        question: "How do I get access to the Course Hub?",
        answer: "After signing up and completing your profile on your User Dashboard, you confirm payment with the administrator. Once payment is verified, your admin grants you Course Hub access. You will then be able to log in and access all course materials.",
    },
    {
        icon: <PaymentIcon fontSize="small" />,
        question: "What plans are available and what do they include?",
        answer: "We offer three plans: Basic (all 16 courses, inbuilt compiler, assignments, DSA training, goal setting, and certification), Standard (everything in Basic plus online coding support), and Premium Individual (everything in Standard plus production project development and live deployment).",
    },
    {
        icon: <DevicesIcon fontSize="small" />,
        question: "Do I need to install anything to start coding?",
        answer: "No. All plans include our inbuilt online compiler — you can write, run, and test code directly in your browser with no local installation needed. This works for all 16 course tracks.",
    },
    {
        icon: <EmojiEventsIcon fontSize="small" />,
        question: "Do I get a certificate after completing a course?",
        answer: "Yes. Upon successful completion of your curriculum, assignments, and online exams, you will receive a professional certification awarded through the platform. Certification is available on all plans.",
    },
    {
        icon: <ScheduleIcon fontSize="small" />,
        question: "Can I learn at my own pace?",
        answer: "Yes. While we provide structured course tracks and assignments, the platform is designed to accommodate flexible learning schedules for both students and working professionals. You can set personal goals and track your progress on your User Dashboard.",
    },
    {
        icon: <CodeIcon fontSize="small" />,
        question: "What is included in the DSA training?",
        answer: "Every plan includes extensive Data Structures and Algorithms training designed to build your coding muscle. This covers arrays, linked lists, trees, graphs, sorting algorithms, recursion, dynamic programming, and more — essential for technical interviews and professional development.",
    },
    {
        icon: <SupportAgentIcon fontSize="small" />,
        question: "What kind of support is available if I get stuck?",
        answer: "Basic plan users have access to course materials and assignments with solutions. Standard plan users additionally get online coding support from instructors when they encounter issues. Premium Individual users get full support plus guided production project development.",
    },
    {
        icon: <HelpOutlineIcon fontSize="small" />,
        question: "Can I upgrade my plan later?",
        answer: "Yes. You can upgrade from Basic to Standard or Premium Individual at any time by contacting your administrator. Your progress and profile data are preserved when you upgrade.",
    },
];

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * StyledHeader
 * Dark branded hero box at the top of the FAQ page.
 */
const StyledHeader = styled(Box)({
    width: "100%",
    textAlign: "center",
    padding: "60px 24px",
    backgroundColor: BRAND.dark,
    color: BRAND.white,
    borderRadius: "16px",
    marginBottom: "40px",
    boxShadow: "0px 10px 30px rgba(0,0,0,0.15)",
    position: "relative",
    overflow: "hidden",
    // Decorative gold circle accent
    "&::after": {
        content: '""',
        position: "absolute",
        right: -60,
        top: -60,
        width: 200,
        height: 200,
        borderRadius: "50%",
        backgroundColor: "rgba(229,201,95,0.08)",
        pointerEvents: "none",
    },
});

/**
 * CustomAccordion
 * Removes default MUI borders and adds a premium card feel.
 * Highlights with brand dark border when expanded.
 */
const CustomAccordion = styled(Accordion)({
    boxShadow: "none",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "12px !important",
    marginBottom: "12px",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    // Remove default MUI divider line between accordions
    "&:before": { display: "none" },
    // Active/expanded state
    "&.Mui-expanded": {
        boxShadow: "0px 4px 20px rgba(0,0,0,0.06)",
        borderColor: BRAND.dark,
        borderLeft: `4px solid ${BRAND.gold}`,
    },
});

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function Faq() {

    // ── State ────────────────────────────────────────────────
    // Tracks which accordion panel is currently expanded.
    // null means all are collapsed.
    const [expanded, setExpanded] = useState(null);

    /**
     * handleChange
     * Toggles the clicked panel open or closed.
     * If the same panel is clicked again, it closes.
     *
     * @param {string} panel - The panel identifier
     */
    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : null);
    };

    // ============================================================
    //  RENDER
    // ============================================================

    return (
        <Container maxWidth="md" sx={{ py: 6 }}>

            {/* ── Hero Header ── */}
            <StyledHeader>

                {/* Icon */}
                <Box
                    sx={{
                        width: 56, height: 56,
                        borderRadius: "50%",
                        backgroundColor: "rgba(229,201,95,0.15)",
                        border: `1px solid rgba(229,201,95,0.3)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                    }}
                >
                    <HelpOutlineIcon sx={{ color: BRAND.gold, fontSize: 28 }} />
                </Box>

                {/* Headline */}
                <Typography
                    variant="h2"
                    sx={{
                        fontSize: { xs: "1.8rem", md: "2.4rem" },
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        fontFamily: "'Georgia', serif",
                        color: BRAND.gold,
                        mb: 1,
                    }}
                >
                    Frequently Asked Questions
                </Typography>

                {/* Subtitle */}
                <Typography
                    sx={{
                        opacity: 0.75,
                        fontWeight: 300,
                        fontSize: "1rem",
                        maxWidth: 500,
                        margin: "0 auto",
                        lineHeight: 1.7,
                    }}
                >
                    Find answers to common questions about our courses,
                    plans, and how the platform works.
                </Typography>

                {/* FAQ count chip */}
                <Chip
                    label={`${FAQ_DATA.length} Questions Answered`}
                    size="small"
                    sx={{
                        mt: 2,
                        backgroundColor: "rgba(229,201,95,0.15)",
                        color: BRAND.gold,
                        fontWeight: 600,
                        border: `1px solid rgba(229,201,95,0.25)`,
                        fontSize: "0.75rem",
                    }}
                />
            </StyledHeader>

            {/* ── Accordion FAQ List ── */}
            <Box>
                {FAQ_DATA.map((item, index) => {
                    const panelId = `panel-${index}`;
                    const isExpanded = expanded === panelId;

                    return (
                        <CustomAccordion
                            key={panelId}
                            expanded={isExpanded}
                            onChange={handleChange(panelId)}
                        >
                            {/* Question row */}
                            <AccordionSummary
                                expandIcon={
                                    <ExpandMoreIcon
                                        sx={{
                                            color: isExpanded ? BRAND.gold : BRAND.dark,
                                            transition: "color 0.2s ease",
                                        }}
                                    />
                                }
                                aria-controls={`${panelId}-content`}
                                id={`${panelId}-header`}
                                sx={{
                                    px: 3, py: 1.5,
                                    // Gold background tint when expanded
                                    backgroundColor: isExpanded
                                        ? "rgba(229,201,95,0.05)"
                                        : "transparent",
                                    borderRadius: "12px",
                                    transition: "background-color 0.2s ease",
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    {/* Category icon */}
                                    <Box
                                        sx={{
                                            color: isExpanded ? BRAND.gold : BRAND.muted,
                                            display: "flex",
                                            transition: "color 0.2s ease",
                                        }}
                                    >
                                        {item.icon}
                                    </Box>

                                    {/* Question text */}
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            color: isExpanded ? BRAND.dark : BRAND.text,
                                            fontSize: "0.95rem",
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {item.question}
                                    </Typography>
                                </Box>
                            </AccordionSummary>

                            {/* Answer */}
                            <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                                <Typography
                                    sx={{
                                        color: BRAND.muted,
                                        lineHeight: 1.8,
                                        fontSize: "0.92rem",
                                        pl: 3.5, // Aligns with question text (past the icon)
                                    }}
                                >
                                    {item.answer}
                                </Typography>
                            </AccordionDetails>
                        </CustomAccordion>
                    );
                })}
            </Box>

        </Container>
    );
}
